import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { onAuthError } from "../api/client";
import { EmptyState, Loading, ErrorState } from "../components/ui/misc";
import { useCan, useSession, useSetSession } from "./session";

/** Redirects to sign-in when there is no session, and to MFA when verification is pending. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: session, isLoading, error, refetch } = useSession();
  const setSession = useSetSession();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(
    () =>
      onAuthError((err) => {
        if (err.status === 401) {
          setSession(null);
          navigate("/login", { replace: true, state: { from: location.pathname, expired: true } });
        } else if (err.code === "MFA_ENROLLMENT_REQUIRED") {
          navigate("/account", { replace: true });
        }
      }),
    [navigate, location.pathname, setSession],
  );

  if (isLoading) return <Loading label="Loading your workspace" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (session.mfaPending) return <Navigate to="/mfa" replace />;
  if (session.mfaEnrollmentRequired && location.pathname !== "/account") return <Navigate to="/account" replace />;
  return <>{children}</>;
}

/** Hides pages the user cannot use. The API enforces the same permissions independently. */
export function RequirePermission({ any, platform, children }: { any?: string[]; platform?: boolean; children: ReactNode }) {
  const { canAny, hasPlatformAccess } = useCan();
  const allowed = platform ? hasPlatformAccess : canAny(...(any ?? []));
  if (!allowed) return <EmptyState title="You don't have access to this page" description="Ask an administrator if you need access." />;
  return <>{children}</>;
}
